import { workups_url } from '../utils/config';
import { sendStreamRetryNotification, sendStreamFinalFailureNotification } from '../utils/streamFailureNotifications';
import { StreamFailureError } from '../common/useStreamProcessorNew';
import { usePersistedDataStore, useLocalDataStore } from '../components/store';
import axiosInstance from '../fetchConfig/axiosConfig';
import { createSaveObject, getDefaultCompLimitForReportType, buildExpandedComps } from '../components/CaseReview/SaveComps/createSaveObject';
import { toast } from '../common/toast';
import { parseNdjsonFromResponse } from './parseStreamDebug';
import logger from '../utils/logger';
import { checkTaskExistence } from '../utils/taskUtils';
import { getCaseStrengthPercentage } from '../utils/caseStrengthCalculator';
import { findCompIndex } from '../components/CaseReview/common/compSheetLookup';
import { savedCompIds } from '../components/CaseReview/common/compAddressLookup';

/**
 * Unified KNN Model fetching function
 * Handles both regular KNN runs and inventory updates with stream processing
 * 
 * @param {Object} updateNegotiationObj - The negotiation object with all necessary data
 * @param {boolean} isInventoryUpdate - Whether this is an inventory update (uses compStreamObject) or regular run (uses globalCompRef)
 * @param {Object} globalCompRef - Reference to global comp object (for regular runs)
 * @param {Object} compStreamObject - Reference to comp stream object (for inventory updates)
 * @param {Function} processStreamWithWorker - Web Worker stream processing function
 * @param {Object} user - User object for saving comps
 * @param {Object} advancedSettings - Advanced settings from localStorage
 * @param {Function} clearUnsavedChanges - Callback to clear unsaved changes (for inventory updates)
 */


// This needs a "kill" functionality if the user exits after running the stream. Currently it will always go through all results.

// This retry logic is WORKING - but it does NOT properly restart where it was left off.
// it needs to restart ONLY for the remaining uids, then update the data object for ONLY the remaining ones without altering the already processed ones.

// NAL cleaning posts to /run_nal_cleaning. Autosave posts to /run_autosave, which also cleans NAL sales.
// Fields the autosave and NAL routes read. Index 0 is the subject, then compLimit comps, then any
// saved comps ranked below that window. The regular sheet keeps every row of ids, adjusted prices,
// and NAL flags so saved comps ranked anywhere can still be matched and checked.
const WORKUPS_SHEET_KEYS = [
  'parcel_id', 'TransactionID', 'ALFlag', 'SaleIssue', 'adj_price', 'adj_IFMV',
  'IFMV', 'IFMV_Town', 'IFMVTown', 'IFMV_Village', 'IFMVVillage',
  'SaleDate', 'SalePrice', 'TimeAdjSalePrice', 'PropertyAssessment', 'AssessmentTown',
];

const FULL_LENGTH_REGULAR_KEYS = new Set(['parcel_id', 'TransactionID', 'ALFlag', 'SaleIssue', 'adj_price', 'adj_IFMV']);

const sheetForWorkupsPost = (sheet, rowCount, fullLengthKeys, savedRows = []) => {
  const slim = {};
  WORKUPS_SHEET_KEYS.forEach((key) => {
    const column = sheet?.[key];
    if (Array.isArray(column)) slim[key] = fullLengthKeys?.has(key) ? column.slice() : [...column.slice(0, rowCount), ...savedRows.map((row) => column[row])];
    else if (column != null && column !== '') slim[key] = column;
  });
  return slim;
};

/** When true, skips processStreamWithWorker, workers, taps, and comp ref updates; only drains + logs the body via parseStreamDebug. Set in .env: REACT_APP_KNN_BYPASS_STREAM_PROCESSOR=true */
const KNN_BYPASS_STREAM_PROCESSOR = false;


export const fetchKNNModel = async ({
  isInventoryUpdate = false,
  globalCompRef,
  compStreamObject,
  processStreamWithWorker,
  user,
  clearUnsavedChanges,
  onlyUid = null,
}) => {
  // Get store functions directly
  const setProcessedCount = useLocalDataStore.getState().setProcessedCount;
  const setIsFetching = usePersistedDataStore.getState().setIsFetching;
  const setMarketingResponse = usePersistedDataStore.getState().setMarketingResponse;
  const setTotalCases = usePersistedDataStore.getState().setTotalCases;
  // const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
  setIsFetching(true);
  try {
    // Set report type to Village if village is not 'All'
    // const queryParams = new URLSearchParams(window.location.search);
    // const village = queryParams.get('village') || 'All';

    // Retry logic for stream processing with extended retry for server downtime
    // When StreamFailureError is thrown (empty 2nd index), we retry from the beginning
    // This handles cases where the upstream starts sending blank chunks mid-stream
    // Also handles server downtime with continuous polling
    // let streamProcessingResponse;
    let retryCount = 0;
    const maxRetries = 4; // 4 retries + 1 initial = 5 total attempts
    const retryDelay = 1000; // 5 seconds between retries for server downtime
    const createNonRetryableStreamError = (message) => {
      const error = new StreamFailureError(message);
      error.isNonRetryable = true;
      return error;
    };
    
    // Helper function to reorder uids with current parcel as 0th index for inventory updates
    const reorderUidsWithCurrentFirst = (uids, currentIndex) => {
      if (!uids || uids.length === 0 || currentIndex < 0 || currentIndex >= uids.length) {
        return uids;
      }
      
      // Start with current parcel at index 0
      const reorderedUids = [uids[currentIndex]];
      
      // Add the rest of the parcels in order, wrapping around
      for (let i = 1; i < uids.length; i++) {
        const nextIndex = (currentIndex + i) % uids.length;
        reorderedUids.push(uids[nextIndex]);
      }
      
      return reorderedUids;
    };

    // Track processed UIDs for incremental retry
    let processedUids = new Set();
    // Comp slots are keyed by uids / parcel id; for HighTaxRun, enrichClientCases
    // reorders `cases` to match `uids` (orderEnrichedCasesByUids). Workups requests omit `cases`.
    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
    let originalUids = negotiationObj.inventoryUpdateRun ? 
      negotiationObj.originalPidOrder : 
      negotiationObj.uids;

    let remainingUids = [...originalUids]; // Start with all UIDs
    
    // For inventory updates, reorder UIDs with current parcel first
    if (negotiationObj.inventoryUpdateRun && negotiationObj.updatedSubject) {
      const currentIndex = originalUids.findIndex(uid => uid === negotiationObj.updatedSubject);
      if (currentIndex >= 0) {
        const reorderedUids = reorderUidsWithCurrentFirst(originalUids, currentIndex);
        logger.log('🔄 Inventory update: Reordering UIDs with current parcel first', {
          originalUids: originalUids.slice(0, 5),
          reorderedUids: reorderedUids.slice(0, 5),
          currentSubject: negotiationObj.updatedSubject,
          currentIndex
        });
        remainingUids = reorderedUids;
      }
    }

    if (onlyUid != null && onlyUid !== '') remainingUids = [onlyUid];
    
    logger.log('🔄 Starting KNN fetch with incremental retry logic:', {
      maxRetries,
      retryDelay: `${retryDelay}ms`,
      totalRetryTime: `${(maxRetries * retryDelay) / 1000} seconds`,
      totalUids: originalUids.length,
      remainingUids: remainingUids.length
    });

    let streamProcessorBypassed = false;

    while (retryCount <= maxRetries) {
      try {
        const currentNegotiationObj = usePersistedDataStore.getState().negotiationObj;
        logger.log(`🔄 KNN Fetch Attempt ${retryCount + 1}/${maxRetries + 1}`, {
          timestamp: new Date().toISOString(),
          reportType: currentNegotiationObj.reportType,
          isInventoryUpdate,
          remainingUids: remainingUids.length,
          processedUids: processedUids.size
        });
        
        // Create request object with only remaining UIDs for incremental retry.
        // Omit `cases` — the ML server does not need case rows on workups requests.
        const requestObj = {
          ...currentNegotiationObj,
          uids: remainingUids, // Only request remaining UIDs
          originalPidOrder: remainingUids, // Update originalPidOrder for inventory updates
          incrementalRetry: retryCount > 0, // Flag to indicate this is a retry
          processedUids: Array.from(processedUids), // Send processed UIDs for reference
        };
        if (onlyUid != null && onlyUid !== '') {
          requestObj.inventoryUpdateRun = 1;
          requestObj.updatedSubject = onlyUid;
          requestObj.manualReview = 1;
        }
        delete requestObj.cases;

        // Make request to workups URL directly
        // Use a custom fetch that bypasses authorizedFetch's retry logic
        // so we can handle all retries in this function
        const sessionToken = localStorage.getItem('sessionToken');
        const headers = {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
        };
        
        const requestBody = JSON.stringify(requestObj);
        const requestSizeMb = new TextEncoder().encode(requestBody).byteLength / (1024 * 1024);
        logger.log('🌐 Making incremental fetch request to workups URL:', {
          url: workups_url,
          remainingUids: remainingUids.length,
          onlyUid: onlyUid ?? null,
          processedUids: processedUids.size,
          isRetry: retryCount > 0,
          requestSizeMb: Number(requestSizeMb.toFixed(3)),
          requestObj: requestObj
        });
        
        let response = await fetch(workups_url, {
          method: "POST",
          headers,
          body: requestBody,
        });

        if (!response.ok) {
          const errorText = await response.text();
          logger.error('❌ Error response:', errorText);
          throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
        }

        const responseHeadersPlain = {};
        response.headers.forEach((value, key) => {
          responseHeadersPlain[key] = value;
        });


        if (KNN_BYPASS_STREAM_PROCESSOR) {
          logger.warn(
            '[KNN_BYPASS] REACT_APP_KNN_BYPASS_STREAM_PROCESSOR=true — parseStreamDebug only; no processStreamWithWorker, no NDJSON tap, no comp ref updates.',
          );
          await parseNdjsonFromResponse(response);

          if (retryCount > 0) {
            toast.dismiss('server-polling');
            toast.success('Server is back online! Request completed successfully.', {
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
              draggable: true,
            });
          }

          streamProcessorBypassed = true;
          break;
        }

        const streamBodyForProcessor = response.body;

        // Use Web Worker for stream processing to prevent main thread blocking
        logger.log('🔄 Using Web Worker for KNN stream processing:', currentNegotiationObj.reportType === 'marketing' ? 'marketing run' : 'regular run');
        
        // Initialize marketing index tracking for marketing runs
        if (currentNegotiationObj.reportType === 'marketing') {
          currentNegotiationObj.currentMarketingIndex = 1;
        }
        
        // For inventory updates, use compStreamObject; for regular runs, use globalCompRef
        let streamObject = isInventoryUpdate ? compStreamObject.current : globalCompRef?.current;
        
        // Initialize the appropriate stream object only if it doesn't exist or has wrong size
        // Always use original UIDs length to maintain consistent indexing
        if (!streamObject || !streamObject.properties || 
            streamObject.properties[0]?.length !== originalUids.length || 
            streamObject.properties[1]?.length !== originalUids.length) {
          logger.log('🔄 Initializing stream object for first time or resizing', {
            hasStreamObject: !!streamObject,
            hasProperties: !!streamObject?.properties,
            currentLength: streamObject?.properties?.[0]?.length || 0,
            expectedLength: originalUids.length
          });
          
          if (!streamObject) {
            streamObject = {};
            if (isInventoryUpdate) {
              compStreamObject.current = streamObject;
            } else if (globalCompRef) {
              globalCompRef.current = streamObject;
            }
          }
          streamObject.properties = [
            new Array(originalUids.length).fill(null),
            new Array(originalUids.length).fill(null)
          ];
          streamObject.updated = new Array(originalUids.length).fill(false);
        } else {
          // Count how many entries are already populated
          const populatedRegular = streamObject.properties?.[0]?.filter(item => item !== null).length || 0;
          const populatedOptimized = streamObject.properties?.[1]?.filter(item => item !== null).length || 0;
          const updatedCount = streamObject.updated?.filter(item => item === true).length || 0;
          
          logger.log('🔄 Stream object already exists, preserving data', {
            existingPropertiesLength: streamObject.properties?.[0]?.length || 0,
            existingUpdatedLength: streamObject.updated?.length || 0,
            populatedRegular,
            populatedOptimized,
            updatedCount
          });
        }
        
        // For regular runs, also initialize compStreamObject for consistency
        if (!isInventoryUpdate) {
          if (!compStreamObject.current) {
            compStreamObject.current = {};
          }
          if (!compStreamObject.current.properties || 
              compStreamObject.current.properties[0]?.length !== originalUids.length) {
            logger.log('🔄 Initializing compStreamObject with correct length:', originalUids.length);
            compStreamObject.current.properties = [
              new Array(originalUids.length).fill(null),
              new Array(originalUids.length).fill(null)
            ];
            compStreamObject.current.updated = new Array(originalUids.length).fill(false);
          }
        }
        
        // Create a Map for O(1) lookup of parcelId to original index
        // This prevents O(n) findIndex calls on every polling update
        const uidToIndexMap = new Map();
        originalUids.forEach((uid, index) => {
          uidToIndexMap.set(uid, index);
        });
        
        // Set up polling callback for incremental updates (like original stream processor)
        const onPollingUpdate = (processedData) => {
          // Handle marketing responses differently
          if (processedData.isMarketing) {
            // For marketing, we don't need to update compStreamObject or globalCompRef
            // The marketing data will be handled by the stateful update callback
            return;
          }
          
          // Clear unsaved changes on first successful data processing
          // This ensures unsaved changes are cleared when KNN actually starts returning data
          if (processedData.index === 0 && isInventoryUpdate) {
            logger.log('🧹 Clearing unsaved changes - KNN processing started successfully');
            // We need to access the unsavedChangesRef from the calling context
            // This will be passed in as a parameter
            if (typeof clearUnsavedChanges === 'function') {
              clearUnsavedChanges();
            }
          }
          
          // Track processed UIDs for incremental retry
          const { index, regularComps, optimizedComps, parcelId } = processedData;
          if (parcelId) {
            processedUids.add(parcelId);
            logger.log(`📊 Processed UID: ${parcelId} (${processedUids.size}/${originalUids.length} total)`);
          }
          
          // Regular KNN processing flow
          if (!isInventoryUpdate) {
            setProcessedCount(processedData.index + 1);
          }
          
          // Update the appropriate stream object based on update type
          // Use Map for O(1) lookup instead of O(n) findIndex
          const originalIndex = uidToIndexMap.get(parcelId);
          if (originalIndex !== undefined && originalIndex >= 0) {
            // Ensure stream object exists before updating
            if (!streamObject || !streamObject.properties) {
              logger.error('❌ Stream object is undefined, cannot update');
              return;
            }
            
            // Update the primary stream object at the original index
            streamObject.properties[0][originalIndex] = regularComps;
            streamObject.properties[1][originalIndex] = optimizedComps;
            streamObject.updated[originalIndex] = true;

            // For regular runs, also update compStreamObject for consistency
            if (!isInventoryUpdate) {
              if (!compStreamObject.current || !compStreamObject.current.properties) {
                logger.error('❌ compStreamObject is undefined, cannot update');
                return;
              }
              compStreamObject.current.properties[0][originalIndex] = regularComps;
              compStreamObject.current.properties[1][originalIndex] = optimizedComps;
              compStreamObject.current.updated[originalIndex] = true;
            }
            
            // logger.log(`✅ Updated stream object at original index ${originalIndex} for UID ${parcelId}`);
          } else {
            logger.warn(`⚠️ Could not find original index for UID ${parcelId}`);
          }
        };
        
        // Set up stateful update callback for real-time UI updates
        const onStatefulUpdate = (updateData) => {
          // Handle server errors
          if (updateData.type === 'server_error') {
            logger.error('🚨 KNN/workups stream server error:', updateData.error);
            toast.error(`KNN/workups error: ${updateData.error ?? 'Unknown server error'}`, {
              position: 'top-right',
              autoClose: false,
            });
            // throw createNonRetryableStreamError(`Server error detected: ${updateData.error}`);
          }
          
          // Handle marketing responses
          if (updateData.data && updateData.data.isMarketing) {
            logger.log('📦 Marketing stateful update processing in fetchKNNModel');
            const { marketingResponse, totalCases, currentIndex } = updateData.data;
            
            // Initialize marketing response array if this is the first response (currentIndex === 1)
            if (currentIndex === 1) {
              if (globalCompRef.current.marketingResponse) {
                globalCompRef.current.marketingResponse = null;
              }
              globalCompRef.current.marketingResponse = new Array(totalCases);
              
              // Update total cases in store
              setTotalCases(totalCases);
            }
            
            if (marketingResponse) {
              // Store the pre-processed marketing response at the correct index
              globalCompRef.current.marketingResponse[currentIndex - 1] = marketingResponse;
              
              // Debounced update for marketing responses (same as original)
              if (globalCompRef.current.marketingUpdateTimer) {
                clearTimeout(globalCompRef.current.marketingUpdateTimer);
              }
              
              globalCompRef.current.marketingUpdateTimer = setTimeout(() => {
                if (globalCompRef.current.marketingResponse) {
                  setMarketingResponse([...globalCompRef.current.marketingResponse.filter(Boolean)]);
                }
                globalCompRef.current.marketingUpdateTimer = null;
              }, 250);
            }
            
            return;
          }
          
        };
        
        // Set up completion callback
        const onCompletion = (completionData) => {
          // Handle server errors in completion callback
          if (completionData && completionData.type === 'server_error') {
            logger.error('🚨 Server error in completion callback:', completionData.error);
            throw createNonRetryableStreamError(`Server error detected: ${completionData.error}`);
          }
          
          logger.log('🎉 KNN Stream processing completed in worker:', completionData);
          // streamProcessingResponse = completionData;
        };
        
        // Process stream with worker (body tapped first so raw lines log before any processor code runs)
        await processStreamWithWorker({
          stream: streamBodyForProcessor,
          negotiationObj: requestObj,
          compStreamObjectRef: streamObject,
          onStatefulUpdate,
          onCompletion,
          onPollingUpdate
        });
        
        // Server came back online, show success notification if we were polling
        if (retryCount > 0) {
          toast.dismiss('server-polling'); // Remove the polling notification
          toast.success('Server is back online! Request completed successfully.', {
            position: 'top-right',
            autoClose: 3000,
            closeOnClick: true,
            draggable: true,
          });
        }
        
        break; // Success, exit retry loop
      } catch (err) {
        logger.error(`❌ Error in attempt ${retryCount + 1}:`, {
          errorType: err.constructor.name,
          errorMessage: err.message,
          errorName: err.name,
          stack: err.stack?.split('\n').slice(0, 3).join('\n')
        });
        
        // Check if this is a retryable error (StreamFailureError or network errors)
        const isStreamFailureRetryable = err instanceof StreamFailureError && !err.isNonRetryable;
        const isRetryableError = isStreamFailureRetryable || 
                                err.message?.includes('network error') ||
                                err.message?.includes('QUIC_PROTOCOL_ERROR') ||
                                err.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                                err.message?.includes('Failed to fetch') ||
                                err.message?.includes('CORS') ||
                                err.name === 'TypeError' && err.message?.includes('network error');
        
        logger.log(`🔍 Error analysis:`, {
          isRetryableError,
          isStreamFailureError: err instanceof StreamFailureError,
          isNonRetryableStreamFailure: err instanceof StreamFailureError && err.isNonRetryable,
          hasNetworkError: err.message?.includes('network error'),
          hasQuicError: err.message?.includes('QUIC_PROTOCOL_ERROR'),
          hasFailedToFetch: err.message?.includes('Failed to fetch'),
          hasCorsError: err.message?.includes('CORS'),
          isTypeError: err.name === 'TypeError'
        });
        
        if (isRetryableError) {
          retryCount++;
          logger.log(`🔄 Retryable error detected, incrementing retry count to ${retryCount}/${maxRetries + 1}`);
          
          if (retryCount <= maxRetries) {
            const errorType = err instanceof StreamFailureError ? 
              (err.message.includes('Server error') ? 'server error' : 
               err.message.includes('timeout') ? 'timeout' : 'empty second index') :
              (err.message?.includes('CORS') ? 'server downtime' : 'network error');
              
            logger.warn(`⚠️ Request failed with ${errorType} (attempt ${retryCount}/${maxRetries + 1}) — retrying in ${retryDelay/1000} seconds`);
            
            // Show user-friendly notification about retry attempt
            toast.info(`Retrying request attempt #${retryCount}`, {
              position: 'top-right',
              autoClose: 10000,
              closeOnClick: true,
              draggable: true
            });
            
            // Update remaining UIDs for incremental retry
            remainingUids = originalUids.filter(uid => !processedUids.has(uid));
            
            // Get fresh negotiation object for retry logic
            const negotiationObjForRetry = usePersistedDataStore.getState().negotiationObj;
            
            // Email notification about retry disabled
            if (retryCount <= 2) {
              await sendStreamRetryNotification({
                streamType: 'General',
                negotiationObj: negotiationObjForRetry,
                retryCount,
                maxRetries,
                errorType: errorType,
                errorMessage: err.message
              });
            }
            
            // For inventory updates, reorder remaining UIDs with current parcel first
            if (negotiationObjForRetry.inventoryUpdateRun && negotiationObjForRetry.updatedSubject) {
              const currentIndex = remainingUids.findIndex(uid => uid === negotiationObjForRetry.updatedSubject);
              if (currentIndex >= 0) {
                const reorderedRemainingUids = reorderUidsWithCurrentFirst(remainingUids, currentIndex);
                logger.log('🔄 Inventory update retry: Reordering remaining UIDs with current parcel first', {
                  remainingUids: remainingUids.slice(0, 5),
                  reorderedRemainingUids: reorderedRemainingUids.slice(0, 5),
                  currentSubject: negotiationObjForRetry.updatedSubject,
                  currentIndex
                });
                remainingUids = reorderedRemainingUids;
              }
            }
            if (onlyUid != null && onlyUid !== '') {
              remainingUids = remainingUids.filter((uid) => uid === onlyUid);
            }
            
            logger.log(`🔄 Incremental retry: ${remainingUids.length} UIDs remaining out of ${originalUids.length} total`);
            
            // If no UIDs remaining, we're done (unless it's a marketing report, which should retry anyway)
            if (remainingUids.length === 0 && negotiationObjForRetry.reportType !== 'marketing') {
              logger.log('✅ All UIDs have been processed, no need to retry');
              break;
            }
            
            // Re-initialize stream objects for remaining UIDs
            const lookupArray = remainingUids;
            const retryStreamObject = isInventoryUpdate ? compStreamObject.current : globalCompRef?.current;
            
            // Only initialize if stream object doesn't exist or has wrong size - preserve existing data
            if (!retryStreamObject.properties || 
                retryStreamObject.properties[0]?.length !== originalUids.length || 
                retryStreamObject.properties[1]?.length !== originalUids.length) {
              
              retryStreamObject.properties = [
                new Array(originalUids.length).fill(null),
                new Array(originalUids.length).fill(null)
              ];
              retryStreamObject.updated = new Array(originalUids.length).fill(false);
              
              // For regular runs, also initialize compStreamObject for consistency
              if (!isInventoryUpdate) {
                if (!compStreamObject.current) {
                  compStreamObject.current = {};
                }
                if (!compStreamObject.current.properties || 
                    compStreamObject.current.properties[0]?.length !== originalUids.length) {
                  logger.log('🔄 Initializing compStreamObject during retry with correct length:', originalUids.length);
                  compStreamObject.current.properties = [
                    new Array(originalUids.length).fill(null),
                    new Array(originalUids.length).fill(null)
                  ];
                  compStreamObject.current.updated = new Array(originalUids.length).fill(false);
                }
              }
            } else {
              // Count how many entries are already populated
              const populatedRegular = retryStreamObject.properties?.[0]?.filter(item => item !== null).length || 0;
              const populatedOptimized = retryStreamObject.properties?.[1]?.filter(item => item !== null).length || 0;
              const updatedCount = retryStreamObject.updated?.filter(item => item === true).length || 0;
              
              logger.log('🔄 Preserving existing stream object data during retry', {
                existingPropertiesLength: retryStreamObject.properties?.[0]?.length || 0,
                existingUpdatedLength: retryStreamObject.updated?.length || 0,
                processedUidsCount: processedUids.size,
                populatedRegular,
                populatedOptimized,
                updatedCount
              });
            }
            
            // Use consistent delay for server downtime polling (5 seconds)
            // This provides regular polling without overwhelming the server
            logger.log(`⏳ Waiting ${retryDelay/1000} seconds before retry attempt ${retryCount + 1}...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            
            // Continue to next iteration to make a new request
            logger.log(`🔄 Proceeding to retry attempt ${retryCount + 1} with ${remainingUids.length} remaining UIDs`);
            continue;
          } else {
            const errorType = err instanceof StreamFailureError ? 
              (err.message.includes('Server error') ? 'server error' : 
               err.message.includes('timeout') ? 'timeout' : 'empty second index') :
              'network error';
              
            logger.error(`🚫 Max retries exceeded for request processing (${errorType}) after 2 minutes of polling`);
            logger.error(`📊 Final retry stats:`, {
              totalAttempts: retryCount,
              maxRetries,
              totalTimeMinutes: (retryCount * retryDelay) / 60000,
              lastError: err.message
            });
            
            // Dismiss the polling notification and show failure
            toast.dismiss('server-polling');
            toast.error('Server has been down for 2 minutes. Please try again later.', {
              position: 'top-right',
              autoClose: 5000,
              closeOnClick: true,
              draggable: true,
            });
            
            // Send email notification about final failure
            const negotiationObjForFailure = usePersistedDataStore.getState().negotiationObj;
            await sendStreamFinalFailureNotification({
              streamType: 'General',
              negotiationObj: negotiationObjForFailure,
              maxRetries,
              errorType: errorType,
              errorMessage: err.message,
              additionalRecipients: ['brennan@aventine.ai']
            });
            
            throw new Error(`Request processing failed after 2 minutes of continuous polling (${errorType})`);
          }
        } else {
          // Non-retryable error, don't retry
          // This could be parsing errors, validation errors, or other non-network issues
          logger.error(`🚫 Non-retryable error detected, giving up immediately:`, {
            errorType: err.constructor.name,
            errorMessage: err.message,
            retryCount,
            maxRetries
          });
          throw err;
        }
      }
    }

    if (streamProcessorBypassed) {
      logger.warn('[KNN_BYPASS] Skipping autosave and normal post-stream completion (no comp data was written).');
      setIsFetching(false);
      return;
    }

    logger.log('=== Stream Processing Complete ===');
    logger.log('✅ KNN fetch completed successfully!', {
      totalAttempts: retryCount + 1,
      finalAttempt: retryCount + 1,
      totalTimeSeconds: ((retryCount + 1) * retryDelay) / 1000,
      isInventoryUpdate,
      incrementalRetryStats: {
        originalUidsCount: originalUids.length,
        processedUidsCount: processedUids.size,
        remainingUidsCount: remainingUids.length,
        completionPercentage: ((processedUids.size / originalUids.length) * 100).toFixed(1) + '%'
      }
    });
    logger.log('Updated stream object:', isInventoryUpdate ? compStreamObject.current : globalCompRef?.current);
    logger.log('================================');

    const negotiationObjForAutosave = usePersistedDataStore.getState().negotiationObj;
    logger.log('negotiationObjForAutosave', negotiationObjForAutosave);
    if (!negotiationObjForAutosave) {
      logger.warn('Autosave skipped: negotiationObj is null');
      return;
    }

    logger.log('[TASK_CREATION] fetchKNNModel: post-stream checkTaskExistence', {
      reportType: negotiationObjForAutosave.reportType,
      casesCount: negotiationObjForAutosave.cases?.length || 0,
    });
    checkTaskExistence();
    const canPostWorkupsSave = !isInventoryUpdate
      && negotiationObjForAutosave.reportType !== 'marketing'
      && negotiationObjForAutosave.reportType !== 'data_analysis';
    const autosave = canPostWorkupsSave
      && negotiationObjForAutosave.manualReview !== true
      && negotiationObjForAutosave.autosave !== false;
    if (canPostWorkupsSave) {
      const regularSheets = globalCompRef?.current?.properties?.[0] || [];
      const optimizedSheets = globalCompRef?.current?.properties?.[1] || [];
      const parsedCompLimit = Number(negotiationObjForAutosave.compLimit);
      const compLimit = negotiationObjForAutosave.compLimit != null && negotiationObjForAutosave.compLimit !== '' && parsedCompLimit > 0
        ? parsedCompLimit
        : getDefaultCompLimitForReportType(negotiationObjForAutosave.reportType);
      const rowCount = compLimit + 1;
      usePersistedDataStore.getState().setCompSheet(regularSheets);
      usePersistedDataStore.getState().setOptimizedComps(optimizedSheets);
      const updates = regularSheets
        .map((regular, index) => {
          if (!regular?.parcel_id?.length) return null;
          const regularSlim = sheetForWorkupsPost(regular, rowCount, FULL_LENGTH_REGULAR_KEYS);
          regularSlim.CaseStrength = getCaseStrengthPercentage(index, negotiationObjForAutosave.reportType).caseStrengthA ?? 0;
          const savedRows = savedCompIds(negotiationObjForAutosave.cases?.[index]?.Comps)
            .map((id) => findCompIndex(optimizedSheets[index], id, negotiationObjForAutosave.reportType))
            .filter((row) => row >= rowCount);
          return [regularSlim, sheetForWorkupsPost(optimizedSheets[index], rowCount, null, savedRows)];
        })
        .filter(Boolean);
      if (updates.length) {
        let postRoute = '/run_nal_cleaning';
        let failureLabel = 'Failed to clean NAL sales';
        try {
          const runUserId = user?.userId ?? negotiationObjForAutosave.UserId;
          const sheetBody = {
            MuniCode: negotiationObjForAutosave.MuniCode,
            TaxYear: negotiationObjForAutosave.TaxYear,
            UserId: runUserId,
            TrainingFlag: (user?.userAccess?.level === 'Training' || negotiationObjForAutosave.TrainingUser === 1) ? 1 : 0,
            runtype: negotiationObjForAutosave.reportType === 'assessment' ? 'Excessive' : (negotiationObjForAutosave.reportType === 'village' ? 'village' : 'Unequal'),
          };
          let postBody = sheetBody;
          if (autosave) {
            postRoute = '/run_autosave';
            failureLabel = 'Failed to autosave comps';
            postBody = {
              ...sheetBody,
              Autosave: true,
              ManualReviewOverride: (negotiationObjForAutosave.manualReviewOverride === 1 || negotiationObjForAutosave.manualReviewOverride === true) ? 1 : 0,
              compLimit: negotiationObjForAutosave.compLimit ?? null,
              expandComps: negotiationObjForAutosave.expandComps === true,
              earliestCutoffDate: negotiationObjForAutosave.earliestCutoffDate,
              latestCutoffDate: negotiationObjForAutosave.latestCutoffDate,
              villageRun: negotiationObjForAutosave.villageRun ?? 0,
            };
          }
          const { data } = await axiosInstance.post(postRoute, {
            ...postBody,
            Updates: updates,
          });
          const cleanedBySubject = new Map((data?.nalCleaned || []).map((item) => [String(item.Subject), item]));
          if (cleanedBySubject.size) {
            const current = usePersistedDataStore.getState().negotiationObj;
            usePersistedDataStore.getState().setNegotiationObj({
              ...current,
              cases: current.cases.map((caseItem) => {
                const cleaned = cleanedBySubject.get(String(caseItem.parcel_id));
                if (!cleaned?.Comps) return caseItem;
                return {
                  ...caseItem,
                  Comps: cleaned.Comps,
                  SelectedIndexRegular: cleaned.SelectedIndexRegular,
                  SelectedIndexOptimized: cleaned.SelectedIndexOptimized,
                };
              }),
            });
          }
          if (autosave && data?.success) {
            const expandComps = negotiationObjForAutosave.expandComps === true;
            const compLimitFromRun = negotiationObjForAutosave.compLimit;
            const effectiveCompLimit = expandComps
              ? (compLimitFromRun != null && compLimitFromRun !== '' ? Number(compLimitFromRun) : getDefaultCompLimitForReportType(negotiationObjForAutosave.reportType))
              : null;
            const manualReviewFlag = expandComps ? 1 : 0;
            const manualReviewOverride = negotiationObjForAutosave.manualReviewOverride === 1 || negotiationObjForAutosave.manualReviewOverride === true;
            const saveDataByIndex = {};
            (negotiationObjForAutosave.cases || []).forEach((item, index) => {
              const optimizedCase = optimizedSheets[index];
              const regularComps = regularSheets[index];
              if (!(optimizedCase?.parcel_id && regularComps?.parcel_id)) return;
              if (!manualReviewOverride && item.ManualReview === 1) return;
              saveDataByIndex[index] = expandComps && effectiveCompLimit != null
                ? createSaveObject({
                    selectedComps: buildExpandedComps(item.Comps, optimizedCase, effectiveCompLimit, negotiationObjForAutosave.reportType),
                    caseIndex: index,
                    regularCompObject: regularComps,
                  })
                : createSaveObject({ optimizedCase, caseIndex: index, regularCompObject: regularComps });
            });
            const current = usePersistedDataStore.getState().negotiationObj;
            usePersistedDataStore.getState().setNegotiationObj({
              ...current,
              cases: current.cases.map((caseItem, index) => {
                const saveData = saveDataByIndex[index];
                const cleaned = cleanedBySubject.get(String(caseItem.parcel_id));
                if (cleaned?.Comps) {
                  if (!saveData?.compsObject) return caseItem;
                  return {
                    ...caseItem,
                    Comps: cleaned.Comps,
                    SelectedIndexRegular: cleaned.SelectedIndexRegular,
                    SelectedIndexOptimized: cleaned.SelectedIndexOptimized,
                    RunDate: new Date().toUTCString(),
                    ManualReview: manualReviewFlag,
                    ReviewedBy: user.userId,
                  };
                }
                if (!saveData?.compsObject) return caseItem;
                return {
                  ...caseItem,
                  Comps: saveData.compsObject,
                  SelectedIndexRegular: saveData.selectedIndexRegular,
                  SelectedIndexOptimized: saveData.selectedIndexOptimized,
                  RunDate: new Date().toUTCString(),
                  ManualReview: manualReviewFlag,
                  ReviewedBy: user.userId,
                };
              }),
            });
          }
          if (autosave && data?.success) {
            toast.success('Comp Autosave completed', {
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
              draggable: true,
            });
          }
        } catch (error) {
          logger.error(failureLabel, error);
          toast.error(`${failureLabel}: ${error.message}`, {
            position: 'top-right',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        }
      }
    }
    // Setting fetching here instead of in the stream parser may be incorrect.
    setIsFetching(false);
  } catch (error) {
    logger.error('Error during KNNModel fetch:', error);
    toast.error(`Failed to retrieve all comps, please refresh and try your request again. Error: ${error.message}`, {
        position: 'top-right',
        autoClose: false,
        closeOnClick: true,
        draggable: true,
    });
    setIsFetching(false);
  }
};

